import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  CloudIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  PencilIcon,
  PlayIcon,
  PlusIcon,
  RocketLaunchIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import { PermissionGate } from '../../hooks/useDynamicPermissions';
import { RequiredFieldLabel } from '../../components/forms';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import ClonePipelineModal from '../../components/ClonePipelineModal';
import {
  AWS_ALL_REGION_CODES,
  AWS_REGION_OPTIONS,
  AWS_SELECT_ALL_REGIONS_VALUE,
} from '../../constants/awsRegions';
import { normalizeAwsDetailsForApi } from '../../utils/engagementAwsDetails';
import {
  GCP_REGION_OPTIONS,
  GCP_SELECT_ALL_REGIONS_VALUE,
} from './gcpRegions';
import { normalizeGcpDetailsForApi } from './engagementGcpDetails';
import ResourceSyncModal from '../../components/ResourceSyncModal';
import { maskSensitiveValue } from '../../utils/maskSensitiveValue';

type SubscriptionPipeline = {
  id: string;
  engagementId: string;
  subscriptionId?: string | null;
  clonedBranchName: string;
  repositoryId: string;
  repositoryName?: string;
  pipelineId: string;
  pipelineName?: string;
  yamlPath?: string;
  lastRunId?: string | null;
  lastExecutionStatus?: string | null;
  lastExecutedAt?: string | null;
  deployedAt?: string;
  createdAt?: string;
  isActive: boolean;
  User?: {
    firstName: string;
    lastName: string;
  } | null;
};

type MaskedSubscription = {
  id: string;
  serviceProviderId: string;
  name: string | null;
  subscriptionKey: string;
  azureSubscriptionId: string | null;
  awsAccountId: string | null;
  gcpProjectId: string | null;
  isActive: boolean;
};

type MaskedAzureDetails = {
  tenantIdSet?: boolean;
  clientIdSet?: boolean;
  clientSecretSet?: boolean;
  tenantId?: string | null;
  clientId?: string | null;
  clientSecret?: string | null;
};

type MaskedAwsDetails = {
  accessKeyIdSet?: boolean;
  secretAccessKeySet?: boolean;
  regionSet?: boolean;
  allRegionsSet?: boolean;
  accessKeyId?: string | null;
  secretAccessKey?: string | null;
  region?: string | null;
};

type MaskedGcpDetails = {
  serviceAccountSet?: boolean;
  regionSet?: boolean;
  allRegionsSet?: boolean;
  serviceAccount?: string | null;
  region?: string | null;
};

type MaskedServiceProvider = {
  id: string;
  engagementId: string;
  name: string | null;
  providerType: 'Azure' | 'AWS' | 'GCP';
  isActive: boolean;
  subscriptions: MaskedSubscription[];
  azureDetails?: MaskedAzureDetails | null;
  awsDetails?: MaskedAwsDetails | null;
  gcpDetails?: MaskedGcpDetails | null;
};

type ProcessAllStepPlan = {
  step: 'syncResources' | 'setupPipeline' | 'triggerPipeline';
  label: string;
  willRun: boolean;
  skipReason?: string;
};

type SubscriptionProcessPlan = {
  providerId: string;
  providerName: string | null;
  providerType: string;
  subscriptionId: string;
  subscriptionName: string | null;
  subscriptionKey: string;
  steps: ProcessAllStepPlan[];
  willRunAnyStep: boolean;
  allStepsComplete: boolean;
};

type ProcessAllPreview = {
  engagementId: string;
  subscriptions: SubscriptionProcessPlan[];
  summary: {
    totalSubscriptions: number;
    subscriptionsWithWork: number;
    subscriptionsFullyComplete: number;
    stepsToRun: {
      syncResources: number;
      setupPipeline: number;
      triggerPipeline: number;
    };
  };
  message: string;
};

type ResolvedVariable = {
  template: {
    id: string;
    name: string;
    displayName: string;
    description?: string;
    type: string;
    dataType: string;
    defaultValue?: string;
    isRequired: boolean;
    isSecret: boolean;
  };
  value?: string;
  resolvedValue?: string;
  actualValue?: string;
  isOverridden: boolean;
};

interface ServiceProvidersTabProps {
  engagementId: string;
  engagementName: string;
  onRefreshEngagement?: () => void;
  /** Hide pipeline/process actions — used on Engagement Edit */
  editMode?: boolean;
}

function subscriptionIdentifierDisplay(
  subscription: MaskedSubscription,
  providerType: string
): string {
  if (providerType === 'AWS') {
    return subscription.awsAccountId || subscription.subscriptionKey;
  }
  if (providerType === 'GCP') {
    return subscription.gcpProjectId || subscription.subscriptionKey;
  }
  return subscription.azureSubscriptionId || subscription.subscriptionKey;
}

const providerColors: Record<string, string> = {
  Azure: 'bg-blue-50 border-blue-200',
  AWS: 'bg-orange-50 border-orange-200',
  GCP: 'bg-green-50 border-green-200',
};

function isLikelyAzureSubscriptionId(value: string): boolean {
  const v = value.trim();
  if (v.length !== 36) return false;
  const hyphenPositions = [8, 13, 18, 23];
  for (let i = 0; i < v.length; i += 1) {
    if (hyphenPositions.includes(i)) {
      if (v[i] !== '-') return false;
    } else {
      const c = v[i];
      const isHexDigit =
        (c >= '0' && c <= '9') || (c >= 'a' && c <= 'f') || (c >= 'A' && c <= 'F');
      if (!isHexDigit) return false;
    }
  }
  return true;
}

function isLikelyAwsAccountId(value: string): boolean {
  const v = value.trim();
  if (v.length !== 12) return false;
  for (let i = 0; i < v.length; i += 1) {
    const c = v[i];
    if (c < '0' || c > '9') return false;
  }
  return true;
}

function credentialStatusLabel(isSet: boolean | undefined): string {
  return isSet ? 'Configured' : 'Missing';
}

function maskedCredentialDisplay(
  maskedValue: string | null | undefined,
  isSet: boolean | undefined
): string {
  if (maskedValue) {
    return maskedValue;
  }
  return isSet ? '—' : 'Missing';
}

function shouldSendCredentialUpdate(
  newValue: string,
  maskedCurrent: string | null | undefined
): boolean {
  const trimmed = newValue.trim();
  if (!trimmed) {
    return false;
  }
  if (maskedCurrent && trimmed === maskedCurrent) {
    return false;
  }
  return true;
}

type AzureMaskedField = 'tenantId' | 'clientId' | 'clientSecret';
type AwsMaskedField = 'accessKeyId' | 'secretAccessKey';
type GcpMaskedField = 'serviceAccount';

function resolveMaskedAzureField(
  provider: MaskedServiceProvider,
  field: AzureMaskedField,
  engagementAzure?: MaskedAzureDetails | null
): string {
  return provider.azureDetails?.[field] || engagementAzure?.[field] || '';
}

function resolveMaskedAwsField(
  provider: MaskedServiceProvider,
  field: AwsMaskedField,
  engagementAws?: MaskedAwsDetails | null
): string {
  return provider.awsDetails?.[field] || engagementAws?.[field] || '';
}

function resolveMaskedGcpField(
  provider: MaskedServiceProvider,
  field: GcpMaskedField,
  engagementGcp?: MaskedGcpDetails | null
): string {
  return provider.gcpDetails?.[field] || engagementGcp?.[field] || '';
}

function getPipelineStatusBadge(status?: string | null) {
  if (!status) {
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
        Not run
      </span>
    );
  }

  const statusLower = status.toLowerCase();
  if (statusLower === 'succeeded') {
    return (
      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
        Succeeded
      </span>
    );
  }
  if (statusLower === 'failed') {
    return (
      <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
        Failed
      </span>
    );
  }
  if (statusLower === 'inprogress' || statusLower === 'in progress') {
    return (
      <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
        In Progress
      </span>
    );
  }
  if (statusLower === 'canceled' || statusLower === 'cancelled') {
    return (
      <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
        Canceled
      </span>
    );
  }

  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
      {status}
    </span>
  );
}

function resolvePipelineForSubscription(
  subscription: MaskedSubscription,
  pipelines: SubscriptionPipeline[],
  providerSubscriptions: MaskedSubscription[]
): SubscriptionPipeline | null {
  const direct = pipelines.find((pipeline) => pipeline.subscriptionId === subscription.id);
  if (direct) {
    return direct;
  }

  const isLikelyDefault =
    subscription.name?.toLowerCase() === 'default' ||
    providerSubscriptions.length === 1 ||
    providerSubscriptions[0]?.id === subscription.id;

  if (isLikelyDefault) {
    return pipelines.find((pipeline) => !pipeline.subscriptionId) ?? null;
  }

  return null;
}

function getJobStatusIcon(result?: string, state?: string) {
  if (result) {
    const resultLower = result.toLowerCase();
    if (resultLower === 'succeeded') return <span className="text-lg text-green-600">✓</span>;
    if (resultLower === 'failed') return <span className="text-lg text-red-600">✗</span>;
    if (resultLower === 'canceled' || resultLower === 'cancelled') {
      return <span className="text-lg text-yellow-600">⊘</span>;
    }
    if (resultLower === 'skipped') return <span className="text-lg text-gray-400">⊝</span>;
  }
  if (state) {
    const stateLower = state.toLowerCase();
    if (stateLower === 'inprogress' || stateLower === 'in progress') {
      return <span className="text-lg text-blue-600">⟳</span>;
    }
    if (stateLower === 'pending') return <span className="text-lg text-gray-400">○</span>;
  }
  return <span className="text-lg text-gray-400">•</span>;
}

function renderInheritedCredentialsNote(
  provider: MaskedServiceProvider,
  engagementCloudDetails: {
    azureDetails?: MaskedAzureDetails | null;
    awsDetails?: MaskedAwsDetails | null;
    gcpDetails?: MaskedGcpDetails | null;
  }
) {
  if (provider.providerType === 'Azure') {
    const azure = provider.azureDetails;
    const engagementAzure = engagementCloudDetails.azureDetails;
    return (
      <div className="mb-4 rounded-md border border-blue-100 bg-blue-50/80 p-3 text-sm text-blue-900">
        <p className="font-medium">Credentials inherited from this Azure service provider</p>
        <p className="mt-1 text-blue-800">
          Tenant ID, Client ID, and Client Secret are shared across all subscriptions under this
          provider. Add only the new subscription&apos;s display name and subscription ID below.
        </p>
        <ul className="mt-2 space-y-1 text-xs text-blue-800">
          <li className="break-all font-mono">
            Tenant ID:{' '}
            {maskedCredentialDisplay(
              resolveMaskedAzureField(provider, 'tenantId', engagementAzure),
              azure?.tenantIdSet || engagementAzure?.tenantIdSet
            )}
          </li>
          <li className="break-all font-mono">
            Client ID:{' '}
            {maskedCredentialDisplay(
              resolveMaskedAzureField(provider, 'clientId', engagementAzure),
              azure?.clientIdSet || engagementAzure?.clientIdSet
            )}
          </li>
          <li className="break-all font-mono">
            Client Secret:{' '}
            {maskedCredentialDisplay(
              resolveMaskedAzureField(provider, 'clientSecret', engagementAzure),
              azure?.clientSecretSet || engagementAzure?.clientSecretSet
            )}
          </li>
        </ul>
      </div>
    );
  }

  if (provider.providerType === 'AWS') {
    const aws = provider.awsDetails;
    const engagementAws = engagementCloudDetails.awsDetails;
    return (
      <div className="mb-4 rounded-md border border-orange-100 bg-orange-50/80 p-3 text-sm text-orange-900">
        <p className="font-medium">Credentials inherited from this AWS service provider</p>
        <p className="mt-1 text-orange-800">
          Access keys and region settings are shared. Enter only the new AWS account ID and display
          name below.
        </p>
        <ul className="mt-2 space-y-1 text-xs text-orange-800">
          <li className="break-all font-mono">
            Access Key ID:{' '}
            {maskedCredentialDisplay(
              resolveMaskedAwsField(provider, 'accessKeyId', engagementAws),
              aws?.accessKeyIdSet || engagementAws?.accessKeyIdSet
            )}
          </li>
          <li className="break-all font-mono">
            Secret Access Key:{' '}
            {maskedCredentialDisplay(
              resolveMaskedAwsField(provider, 'secretAccessKey', engagementAws),
              aws?.secretAccessKeySet || engagementAws?.secretAccessKeySet
            )}
          </li>
          <li>
            Region:{' '}
            {aws?.region ||
              engagementAws?.region ||
              maskedCredentialDisplay(undefined, aws?.regionSet || aws?.allRegionsSet)}
          </li>
        </ul>
      </div>
    );
  }

  const gcp = provider.gcpDetails;
  const engagementGcp = engagementCloudDetails.gcpDetails;
  return (
    <div className="mb-4 rounded-md border border-green-100 bg-green-50/80 p-3 text-sm text-green-900">
      <p className="font-medium">Credentials inherited from this GCP service provider</p>
      <p className="mt-1 text-green-800">
        Service account and region settings are shared. Enter only the new GCP project ID and
        display name below.
      </p>
      <ul className="mt-2 space-y-1 text-xs text-green-800">
        <li className="break-all font-mono">
          Service account key:{' '}
          {maskedCredentialDisplay(
            resolveMaskedGcpField(provider, 'serviceAccount', engagementGcp),
            gcp?.serviceAccountSet || engagementGcp?.serviceAccountSet
          )}
        </li>
        <li>
          Region:{' '}
          {gcp?.region ||
            engagementGcp?.region ||
            maskedCredentialDisplay(undefined, gcp?.regionSet || gcp?.allRegionsSet)}
        </li>
      </ul>
    </div>
  );
}

export default function ServiceProvidersTab({
  engagementId,
  engagementName,
  onRefreshEngagement,
  editMode = false,
}: ServiceProvidersTabProps) {
  const { formatDate, formatDateTime } = useDateFormatter();
  const [providers, setProviders] = useState<MaskedServiceProvider[]>([]);
  const [pipelines, setPipelines] = useState<SubscriptionPipeline[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingAll, setProcessingAll] = useState(false);
  const [busySubscriptionId, setBusySubscriptionId] = useState<string | null>(null);
  const [refreshingPipelineId, setRefreshingPipelineId] = useState<string | null>(null);
  const [deleteLoadingPipelineId, setDeleteLoadingPipelineId] = useState<string | null>(null);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logs, setLogs] = useState<any>(null);
  const [selectedPipeline, setSelectedPipeline] = useState<SubscriptionPipeline | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [showProcessAllModal, setShowProcessAllModal] = useState(false);
  const [processAllPreview, setProcessAllPreview] = useState<ProcessAllPreview | null>(null);
  const [processAllPreviewLoading, setProcessAllPreviewLoading] = useState(false);
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [showAddSubscriptionFor, setShowAddSubscriptionFor] = useState<string | null>(null);
  const [newProvider, setNewProvider] = useState({
    name: '',
    providerType: 'Azure' as 'Azure' | 'AWS' | 'GCP',
    tenantId: '',
    clientId: '',
    clientSecret: '',
    subscriptionId: '',
    accountId: '',
    accessKeyId: '',
    secretAccessKey: '',
    region: '',
    vpcId: '',
    iamRole: '',
    projectId: '',
    serviceAccount: '',
    zone: '',
  });
  const [providerFieldErrors, setProviderFieldErrors] = useState<Record<string, string>>({});
  const [newSubscription, setNewSubscription] = useState({
    name: '',
    subscriptionKey: '',
  });
  const [subscriptionFieldErrors, setSubscriptionFieldErrors] = useState<Record<string, string>>({});
  const [editingSubscriptionId, setEditingSubscriptionId] = useState<string | null>(null);
  const [editSubscriptionForm, setEditSubscriptionForm] = useState({
    name: '',
    subscriptionKey: '',
  });
  const [editSubscriptionFieldErrors, setEditSubscriptionFieldErrors] = useState<
    Record<string, string>
  >({});
  const [savingSubscriptionId, setSavingSubscriptionId] = useState<string | null>(null);
  const [editingProviderId, setEditingProviderId] = useState<string | null>(null);
  const [editProviderForm, setEditProviderForm] = useState({
    name: '',
    tenantId: '',
    clientId: '',
    clientSecret: '',
    accessKeyId: '',
    secretAccessKey: '',
    serviceAccount: '',
  });
  const [editProviderFieldErrors, setEditProviderFieldErrors] = useState<Record<string, string>>({});
  const [editProviderMasked, setEditProviderMasked] = useState({
    tenantId: '',
    clientId: '',
    clientSecret: '',
    accessKeyId: '',
    secretAccessKey: '',
    serviceAccount: '',
  });
  const [savingProviderId, setSavingProviderId] = useState<string | null>(null);
  const [engagementCloudDetails, setEngagementCloudDetails] = useState<{
    azureDetails?: MaskedAzureDetails | null;
    awsDetails?: MaskedAwsDetails | null;
    gcpDetails?: MaskedGcpDetails | null;
  }>({});
  const [engagementStatus, setEngagementStatus] = useState<string | null>(null);
  const [showVariablesModal, setShowVariablesModal] = useState(false);
  const [modalMode, setModalMode] = useState<'clone' | 'trigger'>('clone');
  const [engagementVariables, setEngagementVariables] = useState<ResolvedVariable[]>([]);
  const [variablesLoading, setVariablesLoading] = useState(false);
  const [pipelineActionLoading, setPipelineActionLoading] = useState(false);
  const [selectedSubscriptionForPipeline, setSelectedSubscriptionForPipeline] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [selectedPipelineForTrigger, setSelectedPipelineForTrigger] = useState<SubscriptionPipeline | null>(
    null
  );
  const [excludedVariablesMessage, setExcludedVariablesMessage] = useState<string | null>(null);
  const [showExcludedVariablesAlert, setShowExcludedVariablesAlert] = useState(false);
  const [resourceSyncTarget, setResourceSyncTarget] = useState<{
    subscriptionId: string;
    subscriptionName: string;
  } | null>(null);

  const loadProviders = useCallback(async () => {
    setLoading(true);
    try {
      const engagementResponse = await api.get(`/engagements/${engagementId}`);
      setProviders(engagementResponse.data.serviceProviders || []);
      setEngagementCloudDetails({
        azureDetails: engagementResponse.data.azureDetails ?? null,
        awsDetails: engagementResponse.data.awsDetails ?? null,
        gcpDetails: engagementResponse.data.gcpDetails ?? null,
      });
      setEngagementStatus(engagementResponse.data.status ?? null);

      if (editMode) {
        setPipelines([]);
      } else {
        const pipelinesResponse = await api
          .get(`/pipelines/engagement/${engagementId}`)
          .catch(() => ({ data: [] as SubscriptionPipeline[] }));
        setPipelines(Array.isArray(pipelinesResponse.data) ? pipelinesResponse.data : []);
      }
    } catch {
      toast.error('Failed to load service providers');
    } finally {
      setLoading(false);
    }
  }, [engagementId, editMode]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  const handleCreateProvider = async () => {
    const errs: Record<string, string> = {};

    if (newProvider.providerType === 'Azure') {
      if (!newProvider.tenantId.trim()) errs['tenantId'] = 'Tenant ID is required';
      if (!newProvider.clientId.trim()) errs['clientId'] = 'Client ID is required';
      if (!newProvider.clientSecret.trim()) errs['clientSecret'] = 'Client Secret is required';
      if (!newProvider.subscriptionId.trim()) errs['subscriptionId'] = 'Subscription ID is required';
    } else if (newProvider.providerType === 'AWS') {
      if (!newProvider.accountId.trim()) errs['accountId'] = 'Account ID is required';
      if (!newProvider.region.trim()) errs['region'] = 'Region is required';
      if (!newProvider.accessKeyId.trim()) errs['accessKeyId'] = 'Access Key ID is required';
      if (!newProvider.secretAccessKey.trim()) errs['secretAccessKey'] = 'Secret Access Key is required';
    } else if (newProvider.providerType === 'GCP') {
      if (!newProvider.projectId.trim()) errs['projectId'] = 'Project ID is required';
      if (!newProvider.region.trim()) errs['region'] = 'Region is required';
      if (!newProvider.serviceAccount.trim()) errs['serviceAccount'] = 'Service account key (JSON) is required';
    }

    if (Object.keys(errs).length > 0) {
      setProviderFieldErrors(errs);
      toast.error('Please fill in all required fields');
      return;
    }
    setProviderFieldErrors({});

    try {
      const payload: Record<string, unknown> = {
        name: newProvider.name || `${newProvider.providerType} Provider`,
        providerType: newProvider.providerType,
      };

      if (newProvider.providerType === 'Azure') {
        payload.azureDetails = {
          tenantId: newProvider.tenantId.trim(),
          clientId: newProvider.clientId.trim(),
          clientSecret: newProvider.clientSecret.trim(),
        };
      } else if (newProvider.providerType === 'AWS') {
        const normalized = normalizeAwsDetailsForApi({
          accountId: '',
          region: newProvider.region,
          accessKeyId: newProvider.accessKeyId,
          secretAccessKey: newProvider.secretAccessKey,
          vpcId: newProvider.vpcId,
          iamRole: newProvider.iamRole,
        });
        const { accountId: _ignored, ...providerAws } = normalized;
        payload.awsDetails = providerAws;
      } else {
        const normalized = normalizeGcpDetailsForApi({
          projectId: '',
          region: newProvider.region,
          serviceAccount: newProvider.serviceAccount,
          zone: newProvider.zone,
        });
        const { projectId: _ignored, ...providerGcp } = normalized;
        payload.gcpDetails = providerGcp;
      }

      const created = await api.post(`/engagements/${engagementId}/service-providers`, payload);
      const providerId = created.data.serviceProvider.id;

      if (newProvider.providerType === 'Azure') {
        await api.post(
          `/engagements/${engagementId}/service-providers/${providerId}/subscriptions`,
          {
            name: 'Primary',
            azureSubscriptionId: newProvider.subscriptionId.trim(),
          }
        );
      } else if (newProvider.providerType === 'AWS') {
        await api.post(
          `/engagements/${engagementId}/service-providers/${providerId}/subscriptions`,
          {
            name: 'Primary',
            awsAccountId: newProvider.accountId.trim(),
          }
        );
      } else if (newProvider.providerType === 'GCP') {
        await api.post(
          `/engagements/${engagementId}/service-providers/${providerId}/subscriptions`,
          {
            name: 'Primary',
            gcpProjectId: newProvider.projectId.trim(),
          }
        );
      }

      toast.success('Service provider created');
      setShowAddProvider(false);
      await loadProviders();
      onRefreshEngagement?.();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create service provider');
    }
  };

  const validateNewSubscription = (providerType: string): Record<string, string> => {
    const errs: Record<string, string> = {};
    const name = newSubscription.name.trim();
    const key = newSubscription.subscriptionKey.trim();

    if (!name) {
      errs.name = 'Display name is required';
    }
    if (!key) {
      errs.subscriptionKey = `${subscriptionKeyLabel(providerType)} is required`;
    } else if (providerType === 'Azure' && !isLikelyAzureSubscriptionId(key)) {
      errs.subscriptionKey = 'Enter a valid Azure subscription ID (GUID format)';
    } else if (providerType === 'AWS' && !isLikelyAwsAccountId(key)) {
      errs.subscriptionKey = 'Enter a valid 12-digit AWS account ID';
    }

    return errs;
  };

  const handleCreateSubscription = async (providerId: string, providerType: string) => {
    const errs = validateNewSubscription(providerType);
    if (Object.keys(errs).length > 0) {
      setSubscriptionFieldErrors(errs);
      toast.error('Please fill in all required fields');
      return;
    }
    setSubscriptionFieldErrors({});

    try {
      const body: Record<string, string> = {
        name: newSubscription.name.trim(),
      };

      if (providerType === 'Azure') {
        body.azureSubscriptionId = newSubscription.subscriptionKey.trim();
      } else if (providerType === 'AWS') {
        body.awsAccountId = newSubscription.subscriptionKey.trim();
      } else {
        body.gcpProjectId = newSubscription.subscriptionKey.trim();
      }

      await api.post(
        `/engagements/${engagementId}/service-providers/${providerId}/subscriptions`,
        body
      );
      toast.success('Subscription added');
      setShowAddSubscriptionFor(null);
      setNewSubscription({ name: '', subscriptionKey: '' });
      await loadProviders();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to add subscription');
    }
  };

  const startEditSubscription = (subscription: MaskedSubscription) => {
    cancelEditProvider();
    setShowAddSubscriptionFor(null);
    setSubscriptionFieldErrors({});
    setEditingSubscriptionId(subscription.id);
    setEditSubscriptionForm({
      name: subscription.name || '',
      subscriptionKey: '',
    });
    setEditSubscriptionFieldErrors({});
  };

  const cancelEditSubscription = () => {
    setEditingSubscriptionId(null);
    setEditSubscriptionForm({ name: '', subscriptionKey: '' });
    setEditSubscriptionFieldErrors({});
  };

  const validateEditSubscription = (providerType: string): Record<string, string> => {
    const errs: Record<string, string> = {};
    const name = editSubscriptionForm.name.trim();
    const key = editSubscriptionForm.subscriptionKey.trim();

    if (!name) {
      errs.name = 'Display name is required';
    }
    if (key) {
      if (providerType === 'Azure' && !isLikelyAzureSubscriptionId(key)) {
        errs.subscriptionKey = 'Enter a valid Azure subscription ID (GUID format)';
      } else if (providerType === 'AWS' && !isLikelyAwsAccountId(key)) {
        errs.subscriptionKey = 'Enter a valid 12-digit AWS account ID';
      }
    }

    return errs;
  };

  const handleUpdateSubscription = async (
    providerId: string,
    providerType: string,
    subscriptionId: string
  ) => {
    const errs = validateEditSubscription(providerType);
    if (Object.keys(errs).length > 0) {
      setEditSubscriptionFieldErrors(errs);
      toast.error('Please fix the errors before saving');
      return;
    }
    setEditSubscriptionFieldErrors({});
    setSavingSubscriptionId(subscriptionId);

    try {
      const body: Record<string, string> = {
        name: editSubscriptionForm.name.trim(),
      };
      const key = editSubscriptionForm.subscriptionKey.trim();
      if (key) {
        if (providerType === 'Azure') {
          body.azureSubscriptionId = key;
        } else if (providerType === 'AWS') {
          body.awsAccountId = key;
        } else {
          body.gcpProjectId = key;
        }
      }

      await api.patch(
        `/engagements/${engagementId}/service-providers/${providerId}/subscriptions/${subscriptionId}`,
        body
      );
      toast.success('Subscription updated');
      cancelEditSubscription();
      await loadProviders();
      onRefreshEngagement?.();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update subscription');
    } finally {
      setSavingSubscriptionId(null);
    }
  };

  const cancelEditProvider = () => {
    setEditingProviderId(null);
    setEditProviderForm({
      name: '',
      tenantId: '',
      clientId: '',
      clientSecret: '',
      accessKeyId: '',
      secretAccessKey: '',
      serviceAccount: '',
    });
    setEditProviderMasked({
      tenantId: '',
      clientId: '',
      clientSecret: '',
      accessKeyId: '',
      secretAccessKey: '',
      serviceAccount: '',
    });
    setEditProviderFieldErrors({});
  };

  const startEditProvider = (provider: MaskedServiceProvider) => {
    cancelEditSubscription();
    setShowAddSubscriptionFor(null);
    setEditingProviderId(provider.id);
    setEditProviderForm({
      name: provider.name || '',
      tenantId: '',
      clientId: '',
      clientSecret: '',
      accessKeyId: '',
      secretAccessKey: '',
      serviceAccount: '',
    });
    setEditProviderMasked({
      tenantId: resolveMaskedAzureField(
        provider,
        'tenantId',
        engagementCloudDetails.azureDetails
      ),
      clientId: resolveMaskedAzureField(
        provider,
        'clientId',
        engagementCloudDetails.azureDetails
      ),
      clientSecret: resolveMaskedAzureField(
        provider,
        'clientSecret',
        engagementCloudDetails.azureDetails
      ),
      accessKeyId: resolveMaskedAwsField(
        provider,
        'accessKeyId',
        engagementCloudDetails.awsDetails
      ),
      secretAccessKey: resolveMaskedAwsField(
        provider,
        'secretAccessKey',
        engagementCloudDetails.awsDetails
      ),
      serviceAccount: resolveMaskedGcpField(
        provider,
        'serviceAccount',
        engagementCloudDetails.gcpDetails
      ),
    });
    setEditProviderFieldErrors({});
  };

  const validateEditProvider = (_providerType: string): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (!editProviderForm.name.trim()) {
      errs.name = 'Provider name is required';
    }
    return errs;
  };

  const handleUpdateProvider = async (provider: MaskedServiceProvider) => {
    const errs = validateEditProvider(provider.providerType);
    if (Object.keys(errs).length > 0) {
      setEditProviderFieldErrors(errs);
      toast.error('Please fix the errors before saving');
      return;
    }
    setEditProviderFieldErrors({});
    setSavingProviderId(provider.id);

    try {
      const payload: Record<string, unknown> = {
        name: editProviderForm.name.trim(),
      };

      if (provider.providerType === 'Azure') {
        const azureDetails: Record<string, string> = {};
        if (
          shouldSendCredentialUpdate(editProviderForm.tenantId, editProviderMasked.tenantId)
        ) {
          azureDetails.tenantId = editProviderForm.tenantId.trim();
        }
        if (shouldSendCredentialUpdate(editProviderForm.clientId, editProviderMasked.clientId)) {
          azureDetails.clientId = editProviderForm.clientId.trim();
        }
        if (
          shouldSendCredentialUpdate(editProviderForm.clientSecret, editProviderMasked.clientSecret)
        ) {
          azureDetails.clientSecret = editProviderForm.clientSecret.trim();
        }
        if (Object.keys(azureDetails).length > 0) {
          payload.azureDetails = azureDetails;
        }
      } else if (provider.providerType === 'AWS') {
        const awsDetails: Record<string, string> = {};
        if (
          shouldSendCredentialUpdate(editProviderForm.accessKeyId, editProviderMasked.accessKeyId)
        ) {
          awsDetails.accessKeyId = editProviderForm.accessKeyId.trim();
        }
        if (
          shouldSendCredentialUpdate(
            editProviderForm.secretAccessKey,
            editProviderMasked.secretAccessKey
          )
        ) {
          awsDetails.secretAccessKey = editProviderForm.secretAccessKey.trim();
        }
        if (Object.keys(awsDetails).length > 0) {
          payload.awsDetails = awsDetails;
        }
      } else if (
        shouldSendCredentialUpdate(
          editProviderForm.serviceAccount,
          editProviderMasked.serviceAccount
        )
      ) {
        payload.gcpDetails = { serviceAccount: editProviderForm.serviceAccount.trim() };
      }

      await api.patch(
        `/engagements/${engagementId}/service-providers/${provider.id}`,
        payload
      );
      toast.success('Service provider updated');
      cancelEditProvider();
      await loadProviders();
      onRefreshEngagement?.();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update service provider');
    } finally {
      setSavingProviderId(null);
    }
  };

  const fetchEngagementVariables = async (subscriptionId?: string) => {
    setVariablesLoading(true);
    try {
      const response = await api.get(`/engagements/${engagementId}/variables`, {
        params: subscriptionId ? { subscriptionId } : undefined,
      });
      setEngagementVariables(response.data);
    } catch {
      toast.error('Failed to load variables');
    } finally {
      setVariablesLoading(false);
    }
  };

  const handleClonePipelineClick = async (subscription: MaskedSubscription) => {
    if (engagementStatus && engagementStatus !== 'ACTIVE') {
      toast.error('Pipeline can only be cloned for active engagements');
      return;
    }

    setSelectedSubscriptionForPipeline({
      id: subscription.id,
      name: subscription.name || 'Subscription',
    });
    setSelectedPipelineForTrigger(null);
    await fetchEngagementVariables(subscription.id);
    setModalMode('clone');
    setShowVariablesModal(true);
  };

  const handleTriggerPipelineClick = async (
    subscription: MaskedSubscription,
    pipeline: SubscriptionPipeline
  ) => {
    setSelectedSubscriptionForPipeline({
      id: subscription.id,
      name: subscription.name || 'Subscription',
    });
    setSelectedPipelineForTrigger(pipeline);
    await fetchEngagementVariables(subscription.id);
    setModalMode('trigger');
    setShowVariablesModal(true);
  };

  const handleClonePipelineConfirm = async (
    variables: Array<{ templateId: string; value: string }>,
    excludedVariables?: string[]
  ) => {
    if (!selectedSubscriptionForPipeline) {
      return;
    }

    setPipelineActionLoading(true);
    setBusySubscriptionId(selectedSubscriptionForPipeline.id);

    try {
      if (excludedVariables && excludedVariables.length > 0) {
        setExcludedVariablesMessage(excludedVariables.join(', '));
        setShowExcludedVariablesAlert(true);
        setTimeout(() => setShowExcludedVariablesAlert(false), 60000);
      }

      await api.post(
        `/engagements/${engagementId}/subscriptions/${selectedSubscriptionForPipeline.id}/pipelines/setup`,
        { variables }
      );
      toast.success('Pipeline cloned successfully');
      setShowVariablesModal(false);
      await loadProviders();
      onRefreshEngagement?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to clone pipeline');
    } finally {
      setPipelineActionLoading(false);
      setBusySubscriptionId(null);
    }
  };

  const handleTriggerPipelineConfirm = async (
    variables: Array<{ templateId: string; value: string }>,
    excludedVariables?: string[]
  ) => {
    if (!selectedSubscriptionForPipeline) {
      return;
    }

    setPipelineActionLoading(true);
    setBusySubscriptionId(selectedSubscriptionForPipeline.id);

    try {
      if (excludedVariables && excludedVariables.length > 0) {
        setExcludedVariablesMessage(excludedVariables.join(', '));
        setShowExcludedVariablesAlert(true);
        setTimeout(() => setShowExcludedVariablesAlert(false), 60000);
      }

      const templates = await api.get('/variable-templates');
      const templateMap = new Map(templates.data.map((t: { id: string; name: string }) => [t.id, t.name]));

      const variablesRecord: Record<string, string> = {};
      variables.forEach((v) => {
        const templateName = templateMap.get(v.templateId);
        if (templateName && typeof templateName === 'string') {
          variablesRecord[templateName] = v.value;
        }
      });

      await api.post(
        `/engagements/${engagementId}/subscriptions/${selectedSubscriptionForPipeline.id}/pipelines/trigger`,
        { variables: variablesRecord }
      );
      toast.success('Pipeline triggered successfully');
      setShowVariablesModal(false);
      setTimeout(() => {
        loadProviders();
      }, 2000);
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to trigger pipeline');
    } finally {
      setPipelineActionLoading(false);
      setBusySubscriptionId(null);
    }
  };

  const refreshPipelineStatus = async (pipelineId: string) => {
    setRefreshingPipelineId(pipelineId);
    try {
      await api.post(`/pipelines/${pipelineId}/update-status`);
      toast.success('Pipeline status updated');
      await loadProviders();
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to refresh status');
    } finally {
      setRefreshingPipelineId(null);
    }
  };

  const handleViewLogs = async (pipeline: SubscriptionPipeline) => {
    if (!pipeline.lastRunId) {
      toast.error('No pipeline runs found');
      return;
    }

    setSelectedPipeline(pipeline);
    setLogsLoading(true);
    setShowLogsModal(true);
    setSelectedJobId(null);

    try {
      const response = await api.get(`/pipelines/${pipeline.id}/runs/${pipeline.lastRunId}/logs`);
      const logsData = response.data.data;
      setLogs(logsData);
      if (logsData?.logs?.length > 0) {
        setSelectedJobId(logsData.logs[0].id);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Failed to load logs');
      setLogs(null);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleDeletePipeline = async (pipeline: SubscriptionPipeline) => {
    const pipelineLabel = pipeline.pipelineName || pipeline.repositoryName || 'pipeline';
    const repoLabel = pipeline.repositoryName || pipeline.clonedBranchName;
    if (
      !window.confirm(
        `Are you sure you want to delete the pipeline "${pipelineLabel}"? This will also delete the associated repository "${repoLabel}" and branch "${pipeline.clonedBranchName}" from Azure DevOps. This action cannot be undone.`
      )
    ) {
      return;
    }

    setDeleteLoadingPipelineId(pipeline.id);
    try {
      await api.delete(`/pipelines/${pipeline.id}`);
      toast.success(`Pipeline "${pipelineLabel}" deleted successfully`);
      await loadProviders();
      onRefreshEngagement?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Failed to delete pipeline');
    } finally {
      setDeleteLoadingPipelineId(null);
    }
  };

  const handleProcessAllClick = async () => {
    setProcessAllPreviewLoading(true);
    try {
      const response = await api.get(`/engagements/${engagementId}/process-all/preview`);
      setProcessAllPreview(response.data);
      setShowProcessAllModal(true);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to load process preview');
    } finally {
      setProcessAllPreviewLoading(false);
    }
  };

  const handleProcessAllConfirm = async () => {
    if (!processAllPreview?.summary.subscriptionsWithWork) {
      toast.success('All subscriptions are already complete. Nothing to process.');
      setShowProcessAllModal(false);
      return;
    }

    setProcessingAll(true);
    try {
      const response = await api.post(`/engagements/${engagementId}/process-all`);
      const { processed, succeeded, skipped, failed } = response.data;
      await loadProviders();
      onRefreshEngagement?.();

      if (failed?.length) {
        toast.error(
          `Processed ${processed}: ${succeeded} completed, ${skipped} skipped, ${failed.length} failed`
        );
      } else if (skipped > 0) {
        toast.success(
          `Processed ${processed}: ${succeeded} completed, ${skipped} skipped (all steps already done)`
        );
      } else {
        toast.success(`Processed ${processed} subscription(s) successfully`);
      }
      setShowProcessAllModal(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Batch processing failed');
    } finally {
      setProcessingAll(false);
    }
  };

  const subscriptionKeyLabel = (providerType: string) => {
    if (providerType === 'Azure') return 'Subscription ID';
    if (providerType === 'AWS') return 'Account ID';
    return 'Project ID';
  };

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Loading service providers...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Service Providers</h3>
          <p className="mt-1 text-sm text-gray-500">
            {editMode
              ? 'View and edit subscriptions for each service provider.'
              : 'Manage cloud providers and subscriptions for this engagement.'}
          </p>
        </div>
        <div className="flex gap-2">
          <PermissionGate resource="engagements" action="update">
            <button
              type="button"
              onClick={() => setShowAddProvider(true)}
              className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <PlusIcon className="mr-2 h-4 w-4" />
              Add Provider
            </button>
          </PermissionGate>
          {!editMode && (
            <PermissionGate resource="engagements" action="update">
              <button
                type="button"
                onClick={handleProcessAllClick}
                disabled={processingAll || processAllPreviewLoading || providers.length === 0}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <RocketLaunchIcon className="mr-2 h-4 w-4" />
                {processingAll
                  ? 'Processing...'
                  : processAllPreviewLoading
                    ? 'Loading...'
                    : 'Process All'}
              </button>
            </PermissionGate>
          )}
        </div>
      </div>

      {showExcludedVariablesAlert && excludedVariablesMessage && (
        <div className="rounded-md border-l-4 border-blue-400 bg-blue-50 p-4 shadow-sm">
          <div className="flex">
            <div className="flex-shrink-0">
              <InformationCircleIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-blue-800">Excluded Variables Information</h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  <strong>If required</strong>, the following variables were excluded and must be added
                  manually to Azure DevOps:
                </p>
                <p className="mt-2 rounded border border-blue-200 bg-blue-100 p-2 font-mono text-xs">
                  {excludedVariablesMessage}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowExcludedVariablesAlert(false)}
              className="ml-auto text-blue-500 hover:text-blue-700"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {providers.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center">
          <CloudIcon className="mx-auto h-10 w-10 text-gray-400" />
          <p className="mt-3 text-sm text-gray-600">
            No service providers configured yet. Add a provider to manage multiple subscriptions.
          </p>
        </div>
      )}

      {providers.map((provider) => (
        <div
          key={provider.id}
          className={`rounded-lg border p-5 ${providerColors[provider.providerType] || 'bg-white border-gray-200'}`}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h4 className="text-base font-semibold text-gray-900">
                {provider.name || provider.providerType}
              </h4>
              <p className="text-sm text-gray-600">{provider.providerType} Service Provider</p>
            </div>
            <PermissionGate resource="engagements" action="update">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={
                    savingProviderId === provider.id ||
                    editingProviderId !== null ||
                    editingSubscriptionId !== null
                  }
                  onClick={() => startEditProvider(provider)}
                  className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <PencilIcon className="mr-1.5 h-4 w-4" />
                  Edit Credentials
                </button>
                <button
                  type="button"
                  onClick={() => {
                    cancelEditProvider();
                    cancelEditSubscription();
                    setShowAddSubscriptionFor(provider.id);
                    setNewSubscription({ name: '', subscriptionKey: '' });
                    setSubscriptionFieldErrors({});
                  }}
                  className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <PlusIcon className="mr-1.5 h-4 w-4" />
                  Add Subscription
                </button>
              </div>
            </PermissionGate>
          </div>

          {editingProviderId === provider.id ? (
            <div className="mt-4 rounded-md border border-gray-200 bg-white p-4">
              <p className="mb-3 text-sm font-medium text-gray-900">Edit provider credentials</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <RequiredFieldLabel required>Provider name</RequiredFieldLabel>
                  <input
                    type="text"
                    value={editProviderForm.name}
                    onChange={(e) => {
                      setEditProviderFieldErrors((prev) => {
                        const next = { ...prev };
                        delete next.name;
                        return next;
                      });
                      setEditProviderForm((s) => ({ ...s, name: e.target.value }));
                    }}
                    className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${
                      editProviderFieldErrors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {editProviderFieldErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{editProviderFieldErrors.name}</p>
                  )}
                </div>

                {provider.providerType === 'Azure' && (
                  <>
                    <div>
                      <RequiredFieldLabel>Tenant ID</RequiredFieldLabel>
                      <input
                        type="text"
                        value={editProviderForm.tenantId}
                        onChange={(e) => {
                          setEditProviderFieldErrors((prev) => {
                            const next = { ...prev };
                            delete next.tenantId;
                            return next;
                          });
                          setEditProviderForm((s) => ({ ...s, tenantId: e.target.value }));
                        }}
                        placeholder={editProviderMasked.tenantId}
                        className={`mt-1 w-full rounded-md border px-3 py-2 text-sm placeholder:text-gray-400 ${
                          editProviderFieldErrors.tenantId ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Leave blank to keep the current tenant ID.
                      </p>
                    </div>
                    <div>
                      <RequiredFieldLabel>Client ID</RequiredFieldLabel>
                      <input
                        type="text"
                        value={editProviderForm.clientId}
                        onChange={(e) => {
                          setEditProviderFieldErrors((prev) => {
                            const next = { ...prev };
                            delete next.clientId;
                            return next;
                          });
                          setEditProviderForm((s) => ({ ...s, clientId: e.target.value }));
                        }}
                        placeholder={editProviderMasked.clientId}
                        className={`mt-1 w-full rounded-md border px-3 py-2 text-sm placeholder:text-gray-400 ${
                          editProviderFieldErrors.clientId ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Leave blank to keep the current client ID.
                      </p>
                    </div>
                    <div className="sm:col-span-2">
                      <RequiredFieldLabel>Client Secret</RequiredFieldLabel>
                      <input
                        type="password"
                        value={editProviderForm.clientSecret}
                        onChange={(e) => {
                          setEditProviderFieldErrors((prev) => {
                            const next = { ...prev };
                            delete next.clientSecret;
                            return next;
                          });
                          setEditProviderForm((s) => ({ ...s, clientSecret: e.target.value }));
                        }}
                        placeholder={editProviderMasked.clientSecret}
                        className={`mt-1 w-full rounded-md border px-3 py-2 text-sm placeholder:text-gray-400 ${
                          editProviderFieldErrors.clientSecret ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Leave blank to keep the current client secret.
                      </p>
                    </div>
                  </>
                )}

                {provider.providerType === 'AWS' && (
                  <>
                    <div>
                      <RequiredFieldLabel>Access Key ID</RequiredFieldLabel>
                      <input
                        type="text"
                        value={editProviderForm.accessKeyId}
                        onChange={(e) =>
                          setEditProviderForm((s) => ({ ...s, accessKeyId: e.target.value }))
                        }
                        placeholder={editProviderMasked.accessKeyId}
                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400"
                      />
                    </div>
                    <div>
                      <RequiredFieldLabel>Secret Access Key</RequiredFieldLabel>
                      <input
                        type="password"
                        value={editProviderForm.secretAccessKey}
                        onChange={(e) =>
                          setEditProviderForm((s) => ({ ...s, secretAccessKey: e.target.value }))
                        }
                        placeholder={editProviderMasked.secretAccessKey}
                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400"
                      />
                    </div>
                  </>
                )}

                {provider.providerType === 'GCP' && (
                  <div className="sm:col-span-2">
                    <RequiredFieldLabel>Service account key (JSON)</RequiredFieldLabel>
                    <textarea
                      rows={4}
                      value={editProviderForm.serviceAccount}
                      onChange={(e) =>
                        setEditProviderForm((s) => ({ ...s, serviceAccount: e.target.value }))
                      }
                      placeholder={editProviderMasked.serviceAccount}
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm placeholder:text-gray-400"
                    />
                  </div>
                )}
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  disabled={savingProviderId === provider.id}
                  onClick={() => handleUpdateProvider(provider)}
                  className="rounded-md bg-indigo-600 px-3 py-2 text-sm text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {savingProviderId === provider.id ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  disabled={savingProviderId === provider.id}
                  onClick={cancelEditProvider}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-4 rounded-md border border-gray-200 bg-white/70 p-3 text-sm">
              <p className="font-medium text-gray-900">Provider credentials</p>
              {provider.providerType === 'Azure' && (
                <ul className="mt-2 space-y-1 text-xs text-gray-700">
                  <li className="break-all">
                    Tenant ID:{' '}
                    <span className="font-mono">
                      {maskedCredentialDisplay(
                        resolveMaskedAzureField(
                          provider,
                          'tenantId',
                          engagementCloudDetails.azureDetails
                        ),
                        provider.azureDetails?.tenantIdSet ||
                          engagementCloudDetails.azureDetails?.tenantIdSet
                      )}
                    </span>
                  </li>
                  <li className="break-all">
                    Client ID:{' '}
                    <span className="font-mono">
                      {maskedCredentialDisplay(
                        resolveMaskedAzureField(
                          provider,
                          'clientId',
                          engagementCloudDetails.azureDetails
                        ),
                        provider.azureDetails?.clientIdSet ||
                          engagementCloudDetails.azureDetails?.clientIdSet
                      )}
                    </span>
                  </li>
                  <li className="break-all">
                    Client Secret:{' '}
                    <span className="font-mono">
                      {maskedCredentialDisplay(
                        resolveMaskedAzureField(
                          provider,
                          'clientSecret',
                          engagementCloudDetails.azureDetails
                        ),
                        provider.azureDetails?.clientSecretSet ||
                          engagementCloudDetails.azureDetails?.clientSecretSet
                      )}
                    </span>
                  </li>
                </ul>
              )}
              {provider.providerType === 'AWS' && (
                <ul className="mt-2 space-y-1 text-xs text-gray-700">
                  <li className="break-all">
                    Access Key ID:{' '}
                    <span className="font-mono">
                      {maskedCredentialDisplay(
                        resolveMaskedAwsField(
                          provider,
                          'accessKeyId',
                          engagementCloudDetails.awsDetails
                        ),
                        provider.awsDetails?.accessKeyIdSet ||
                          engagementCloudDetails.awsDetails?.accessKeyIdSet
                      )}
                    </span>
                  </li>
                  <li className="break-all">
                    Secret Access Key:{' '}
                    <span className="font-mono">
                      {maskedCredentialDisplay(
                        resolveMaskedAwsField(
                          provider,
                          'secretAccessKey',
                          engagementCloudDetails.awsDetails
                        ),
                        provider.awsDetails?.secretAccessKeySet ||
                          engagementCloudDetails.awsDetails?.secretAccessKeySet
                      )}
                    </span>
                  </li>
                  <li>
                    Region:{' '}
                    {provider.awsDetails?.region ||
                      engagementCloudDetails.awsDetails?.region ||
                      maskedCredentialDisplay(
                        undefined,
                        provider.awsDetails?.regionSet || provider.awsDetails?.allRegionsSet
                      )}
                  </li>
                </ul>
              )}
              {provider.providerType === 'GCP' && (
                <ul className="mt-2 space-y-1 text-xs text-gray-700">
                  <li className="break-all">
                    Service account key:{' '}
                    <span className="font-mono">
                      {maskedCredentialDisplay(
                        resolveMaskedGcpField(
                          provider,
                          'serviceAccount',
                          engagementCloudDetails.gcpDetails
                        ),
                        provider.gcpDetails?.serviceAccountSet ||
                          engagementCloudDetails.gcpDetails?.serviceAccountSet
                      )}
                    </span>
                  </li>
                  <li>
                    Region:{' '}
                    {provider.gcpDetails?.region ||
                      engagementCloudDetails.gcpDetails?.region ||
                      maskedCredentialDisplay(
                        undefined,
                        provider.gcpDetails?.regionSet || provider.gcpDetails?.allRegionsSet
                      )}
                  </li>
                </ul>
              )}
            </div>
          )}

          {showAddSubscriptionFor === provider.id && (
            <div className="mt-4 rounded-md border border-gray-200 bg-white p-4">
              {renderInheritedCredentialsNote(provider, engagementCloudDetails)}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <RequiredFieldLabel required>Display name</RequiredFieldLabel>
                  <input
                    type="text"
                    value={newSubscription.name}
                    onChange={(e) => {
                      setSubscriptionFieldErrors((prev) => {
                        const next = { ...prev };
                        delete next.name;
                        return next;
                      });
                      setNewSubscription((s) => ({ ...s, name: e.target.value }));
                    }}
                    placeholder="e.g., Production, Staging"
                    className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${
                      subscriptionFieldErrors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {subscriptionFieldErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{subscriptionFieldErrors.name}</p>
                  )}
                </div>
                <div>
                  <RequiredFieldLabel required>
                    {subscriptionKeyLabel(provider.providerType)}
                  </RequiredFieldLabel>
                  <input
                    type="text"
                    value={newSubscription.subscriptionKey}
                    onChange={(e) => {
                      setSubscriptionFieldErrors((prev) => {
                        const next = { ...prev };
                        delete next.subscriptionKey;
                        return next;
                      });
                      setNewSubscription((s) => ({ ...s, subscriptionKey: e.target.value }));
                    }}
                    placeholder={
                      provider.providerType === 'Azure'
                        ? 'e.g., f24f77e6-e819-4455-8dfc-fe18a8f2d54c'
                        : provider.providerType === 'AWS'
                          ? 'e.g., 123456789012'
                          : 'e.g., my-gcp-project-123456'
                    }
                    className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${
                      subscriptionFieldErrors.subscriptionKey ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {subscriptionFieldErrors.subscriptionKey && (
                    <p className="mt-1 text-sm text-red-600">
                      {subscriptionFieldErrors.subscriptionKey}
                    </p>
                  )}
                  {provider.providerType === 'Azure' && (
                    <p className="mt-1 text-xs text-gray-500">
                      Azure portal → Subscriptions → copy the subscription ID (GUID).
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => handleCreateSubscription(provider.id, provider.providerType)}
                  className="rounded-md bg-indigo-600 px-3 py-2 text-sm text-white hover:bg-indigo-700"
                >
                  Save Subscription
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddSubscriptionFor(null);
                    setSubscriptionFieldErrors({});
                  }}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="mt-4 space-y-3">
            {provider.subscriptions.length === 0 ? (
              <p className="text-sm text-gray-500">No subscriptions under this provider.</p>
            ) : (
              provider.subscriptions.map((subscription) => {
                const pipeline = resolvePipelineForSubscription(
                  subscription,
                  pipelines,
                  provider.subscriptions
                );

                return (
                <div
                  key={subscription.id}
                  className="rounded-md border border-gray-200 bg-white p-4"
                >
                  {editingSubscriptionId === subscription.id ? (
                    <div>
                      <p className="mb-3 text-sm font-medium text-gray-900">Edit subscription</p>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <RequiredFieldLabel required>Display name</RequiredFieldLabel>
                          <input
                            type="text"
                            value={editSubscriptionForm.name}
                            onChange={(e) => {
                              setEditSubscriptionFieldErrors((prev) => {
                                const next = { ...prev };
                                delete next.name;
                                return next;
                              });
                              setEditSubscriptionForm((s) => ({ ...s, name: e.target.value }));
                            }}
                            placeholder="e.g., Production, Staging"
                            className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${
                              editSubscriptionFieldErrors.name ? 'border-red-500' : 'border-gray-300'
                            }`}
                          />
                          {editSubscriptionFieldErrors.name && (
                            <p className="mt-1 text-sm text-red-600">
                              {editSubscriptionFieldErrors.name}
                            </p>
                          )}
                        </div>
                        <div>
                          <RequiredFieldLabel>
                            {subscriptionKeyLabel(provider.providerType)}
                          </RequiredFieldLabel>
                          <input
                            type="text"
                            value={editSubscriptionForm.subscriptionKey}
                            onChange={(e) => {
                              setEditSubscriptionFieldErrors((prev) => {
                                const next = { ...prev };
                                delete next.subscriptionKey;
                                return next;
                              });
                              setEditSubscriptionForm((s) => ({
                                ...s,
                                subscriptionKey: e.target.value,
                              }));
                            }}
                            placeholder={maskSensitiveValue(
                              subscriptionIdentifierDisplay(subscription, provider.providerType)
                            )}
                            className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${
                              editSubscriptionFieldErrors.subscriptionKey
                                ? 'border-red-500'
                                : 'border-gray-300'
                            }`}
                          />
                          {editSubscriptionFieldErrors.subscriptionKey && (
                            <p className="mt-1 text-sm text-red-600">
                              {editSubscriptionFieldErrors.subscriptionKey}
                            </p>
                          )}
                          <p className="mt-1 text-xs text-gray-500">
                            Leave blank to keep the current{' '}
                            {subscriptionKeyLabel(provider.providerType).toLowerCase()}.
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 flex gap-2">
                        <button
                          type="button"
                          disabled={savingSubscriptionId === subscription.id}
                          onClick={() =>
                            handleUpdateSubscription(
                              provider.id,
                              provider.providerType,
                              subscription.id
                            )
                          }
                          className="rounded-md bg-indigo-600 px-3 py-2 text-sm text-white hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {savingSubscriptionId === subscription.id ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button
                          type="button"
                          disabled={savingSubscriptionId === subscription.id}
                          onClick={cancelEditSubscription}
                          className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-gray-900">
                        {subscription.name || 'Subscription'}
                      </p>
                      <p className="text-xs text-gray-500 break-all">
                        {subscriptionKeyLabel(provider.providerType)}:{' '}
                        {maskSensitiveValue(
                          subscriptionIdentifierDisplay(subscription, provider.providerType)
                        )}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <PermissionGate resource="engagements" action="update">
                        <button
                          type="button"
                          disabled={
                            savingSubscriptionId === subscription.id ||
                            editingSubscriptionId !== null ||
                            editingProviderId !== null
                          }
                          onClick={() => startEditSubscription(subscription)}
                          className="inline-flex items-center rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <PencilIcon className="mr-1 h-4 w-4" />
                          Edit
                        </button>
                      </PermissionGate>
                      {!editMode && (
                        <>
                      <button
                        type="button"
                        disabled={busySubscriptionId === subscription.id}
                        onClick={() =>
                          setResourceSyncTarget({
                            subscriptionId: subscription.id,
                            subscriptionName: subscription.name || 'Subscription',
                          })
                        }
                        className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ArrowPathIcon className="mr-1 h-4 w-4" />
                        Sync Resources
                      </button>
                      <button
                        type="button"
                        disabled={busySubscriptionId === subscription.id || !!pipeline}
                        onClick={() => handleClonePipelineClick(subscription)}
                        className="inline-flex items-center rounded-md border border-transparent bg-green-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Clone Pipeline
                      </button>
                      <button
                        type="button"
                        disabled={busySubscriptionId === subscription.id || !pipeline}
                        onClick={() => pipeline && handleTriggerPipelineClick(subscription, pipeline)}
                        className="inline-flex items-center rounded-md border border-transparent bg-green-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <PlayIcon className="mr-1 h-4 w-4" />
                        Trigger
                      </button>
                        </>
                      )}
                    </div>
                  </div>

                  {!editMode && (
                  <div className="mt-4 border-t border-gray-100 pt-4">
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-medium text-gray-900">Pipeline</p>
                      {pipeline && (
                        <button
                          type="button"
                          onClick={() => refreshPipelineStatus(pipeline.id)}
                          disabled={refreshingPipelineId === pipeline.id}
                          className="inline-flex items-center rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <ArrowPathIcon
                            className={`mr-1 h-3.5 w-3.5 ${
                              refreshingPipelineId === pipeline.id ? 'animate-spin' : ''
                            }`}
                          />
                          Refresh Status
                        </button>
                      )}
                    </div>

                    {!pipeline ? (
                      <p className="text-sm text-gray-500">
                        No pipeline configured for this subscription. Click Clone Pipeline to set one up.
                      </p>
                    ) : (
                      <div className="mt-2 space-y-3 rounded-md bg-gray-50 p-3 text-sm text-gray-700">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-gray-900">
                            {pipeline.pipelineName || pipeline.repositoryName || 'Monitoring pipeline'}
                          </span>
                          {pipeline.isActive ? (
                            <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                              <CheckCircleIcon className="mr-1 h-3.5 w-3.5" />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                              Inactive
                            </span>
                          )}
                          {getPipelineStatusBadge(pipeline.lastExecutionStatus)}
                        </div>

                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          <div>
                            <span className="text-gray-500">Repository:</span>{' '}
                            <span className="font-mono text-xs">
                              {pipeline.repositoryName || pipeline.clonedBranchName}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Branch:</span>{' '}
                            <span className="font-mono text-xs">{pipeline.clonedBranchName}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">YAML:</span>{' '}
                            <span className="font-mono text-xs">{pipeline.yamlPath || '—'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Repository ID:</span>{' '}
                            <span className="font-mono text-xs break-all">{pipeline.repositoryId}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Pipeline ID:</span>{' '}
                            <span className="font-mono text-xs break-all">{pipeline.pipelineId}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Build ID:</span>{' '}
                            {pipeline.lastRunId ? (
                              <button
                                type="button"
                                onClick={() => handleViewLogs(pipeline)}
                                className="font-mono text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                title="Click to view build logs"
                              >
                                {pipeline.lastRunId}
                              </button>
                            ) : (
                              <span className="font-mono text-xs text-gray-400">Not triggered yet</span>
                            )}
                          </div>
                          <div>
                            <span className="text-gray-500">Last run:</span>{' '}
                            <span className="text-xs">
                              {pipeline.lastExecutedAt
                                ? formatDateTime(pipeline.lastExecutedAt)
                                : 'Never'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Created:</span>{' '}
                            <span className="text-xs">
                              {pipeline.deployedAt || pipeline.createdAt
                                ? formatDate(pipeline.deployedAt || pipeline.createdAt!)
                                : '—'}
                              {pipeline.User
                                ? ` by ${pipeline.User.firstName} ${pipeline.User.lastName}`
                                : ''}
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-3">
                          {pipeline.lastRunId && (
                            <button
                              type="button"
                              onClick={() => handleViewLogs(pipeline)}
                              disabled={logsLoading && selectedPipeline?.id === pipeline.id}
                              className="inline-flex items-center rounded-md border border-blue-300 bg-blue-50 px-2.5 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                            >
                              <DocumentTextIcon className="mr-1 h-4 w-4" />
                              View Logs
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleDeletePipeline(pipeline)}
                            disabled={
                              deleteLoadingPipelineId === pipeline.id ||
                              busySubscriptionId === subscription.id
                            }
                            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:border-red-300 hover:text-red-600 disabled:opacity-50"
                          >
                            {deleteLoadingPipelineId === pipeline.id ? (
                              <>
                                <ArrowPathIcon className="mr-1 h-4 w-4 animate-spin" />
                                Deleting...
                              </>
                            ) : (
                              <>
                                <TrashIcon className="mr-1 h-4 w-4" />
                                Delete
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  )}
                    </>
                  )}
                </div>
              );
              })
            )}
          </div>
        </div>
      ))}

      {showAddProvider && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-medium text-gray-900">Add Service Provider</h3>
            <p className="mt-1 text-sm text-gray-500">
              Required fields are marked with an asterisk.
            </p>
            <div className="mt-4 space-y-4">
              <div>
                <RequiredFieldLabel>Provider name</RequiredFieldLabel>
                <input
                  type="text"
                  placeholder="e.g. Customer AWS"
                  value={newProvider.name}
                  onChange={(e) => setNewProvider((s) => ({ ...s, name: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <RequiredFieldLabel required>Provider type</RequiredFieldLabel>
                <select
                  value={newProvider.providerType}
                  onChange={(e) => {
                    setProviderFieldErrors({});
                    setNewProvider((s) => ({
                      ...s,
                      providerType: e.target.value as 'Azure' | 'AWS' | 'GCP',
                    }));
                  }}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="Azure">Azure</option>
                  <option value="AWS">AWS</option>
                  <option value="GCP">GCP</option>
                </select>
              </div>

              {newProvider.providerType === 'Azure' && (
                <div className="grid grid-cols-1 gap-4 rounded-lg bg-blue-50 p-4 sm:grid-cols-2">
                  <div>
                    <RequiredFieldLabel required>Tenant ID</RequiredFieldLabel>
                    <input
                      type="text"
                      value={newProvider.tenantId}
                      onChange={(e) => setNewProvider((s) => ({ ...s, tenantId: e.target.value }))}
                      className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${providerFieldErrors.tenantId ? 'border-red-500' : 'border-gray-300'}`}
                    />
                  </div>
                  <div>
                    <RequiredFieldLabel required>Client ID</RequiredFieldLabel>
                    <input
                      type="text"
                      value={newProvider.clientId}
                      onChange={(e) => setNewProvider((s) => ({ ...s, clientId: e.target.value }))}
                      className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${providerFieldErrors.clientId ? 'border-red-500' : 'border-gray-300'}`}
                    />
                  </div>
                  <div>
                    <RequiredFieldLabel required>Client Secret</RequiredFieldLabel>
                    <input
                      type="password"
                      value={newProvider.clientSecret}
                      onChange={(e) => setNewProvider((s) => ({ ...s, clientSecret: e.target.value }))}
                      className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${providerFieldErrors.clientSecret ? 'border-red-500' : 'border-gray-300'}`}
                    />
                  </div>
                  <div>
                    <RequiredFieldLabel required>Subscription ID</RequiredFieldLabel>
                    <input
                      type="text"
                      value={newProvider.subscriptionId}
                      onChange={(e) => setNewProvider((s) => ({ ...s, subscriptionId: e.target.value }))}
                      className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${providerFieldErrors.subscriptionId ? 'border-red-500' : 'border-gray-300'}`}
                    />
                  </div>
                </div>
              )}

              {newProvider.providerType === 'AWS' && (
                <div className="grid grid-cols-1 gap-4 rounded-lg bg-orange-50 p-4 sm:grid-cols-2">
                  <div>
                    <RequiredFieldLabel required>Account ID</RequiredFieldLabel>
                    <input
                      type="text"
                      value={newProvider.accountId}
                      onChange={(e) => setNewProvider((s) => ({ ...s, accountId: e.target.value }))}
                      placeholder="Enter AWS account ID"
                      className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${providerFieldErrors.accountId ? 'border-red-500' : 'border-gray-300'}`}
                    />
                  </div>
                  <div>
                    <RequiredFieldLabel required>Region</RequiredFieldLabel>
                    <select
                      value={newProvider.region}
                      onChange={(e) => setNewProvider((s) => ({ ...s, region: e.target.value }))}
                      className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${providerFieldErrors.region ? 'border-red-500' : 'border-gray-300'}`}
                    >
                      <option value="">Select region</option>
                      <option value={AWS_SELECT_ALL_REGIONS_VALUE}>Select All Regions</option>
                      {AWS_REGION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <RequiredFieldLabel required>Access Key ID</RequiredFieldLabel>
                    <input
                      type="text"
                      value={newProvider.accessKeyId}
                      onChange={(e) => setNewProvider((s) => ({ ...s, accessKeyId: e.target.value }))}
                      placeholder="Enter access key ID"
                      className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${providerFieldErrors.accessKeyId ? 'border-red-500' : 'border-gray-300'}`}
                    />
                  </div>
                  <div>
                    <RequiredFieldLabel required>Secret Access Key</RequiredFieldLabel>
                    <input
                      type="password"
                      value={newProvider.secretAccessKey}
                      onChange={(e) => setNewProvider((s) => ({ ...s, secretAccessKey: e.target.value }))}
                      placeholder="Enter secret access key"
                      className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${providerFieldErrors.secretAccessKey ? 'border-red-500' : 'border-gray-300'}`}
                    />
                  </div>
                  <div>
                    <RequiredFieldLabel optionalHint="optional">VPC ID</RequiredFieldLabel>
                    <input
                      type="text"
                      value={newProvider.vpcId}
                      onChange={(e) => setNewProvider((s) => ({ ...s, vpcId: e.target.value }))}
                      placeholder="e.g., vpc-12345678"
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <RequiredFieldLabel optionalHint="optional">IAM Role</RequiredFieldLabel>
                    <input
                      type="text"
                      value={newProvider.iamRole}
                      onChange={(e) => setNewProvider((s) => ({ ...s, iamRole: e.target.value }))}
                      placeholder="e.g., role ARN"
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              )}

              {newProvider.providerType === 'GCP' && (
                <div className="grid grid-cols-1 gap-4 rounded-lg bg-green-50 p-4 sm:grid-cols-2">
                  <div>
                    <RequiredFieldLabel required>Project ID</RequiredFieldLabel>
                    <input
                      type="text"
                      value={newProvider.projectId}
                      onChange={(e) => setNewProvider((s) => ({ ...s, projectId: e.target.value }))}
                      placeholder="e.g., my-gcp-project-123456"
                      className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${providerFieldErrors.projectId ? 'border-red-500' : 'border-gray-300'}`}
                    />
                  </div>
                  <div>
                    <RequiredFieldLabel required>Region</RequiredFieldLabel>
                    <select
                      value={newProvider.region}
                      onChange={(e) => setNewProvider((s) => ({ ...s, region: e.target.value }))}
                      className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${providerFieldErrors.region ? 'border-red-500' : 'border-gray-300'}`}
                    >
                      <option value="">Select region</option>
                      <option value={GCP_SELECT_ALL_REGIONS_VALUE}>All regions (standard commercial list)</option>
                      {GCP_REGION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label} ({opt.value})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <RequiredFieldLabel>Zone</RequiredFieldLabel>
                    <input
                      type="text"
                      value={newProvider.zone}
                      onChange={(e) => setNewProvider((s) => ({ ...s, zone: e.target.value }))}
                      placeholder="e.g., us-central1-a"
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <RequiredFieldLabel required>Service account key (JSON)</RequiredFieldLabel>
                    <textarea
                      value={newProvider.serviceAccount}
                      onChange={(e) => setNewProvider((s) => ({ ...s, serviceAccount: e.target.value }))}
                      placeholder="Paste the entire contents of the .json key from GCP (IAM → Service account → Keys). The email alone is not enough."
                      className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${providerFieldErrors.serviceAccount ? 'border-red-500' : 'border-gray-300'}`}
                      rows={4}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowAddProvider(false)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateProvider}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700"
              >
                Create Provider
              </button>
            </div>
          </div>
        </div>
      )}

      {showProcessAllModal && processAllPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-medium text-gray-900">Process All Subscriptions</h3>
            <p className="mt-2 whitespace-pre-line text-sm text-gray-600">{processAllPreview.message}</p>

            <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
              <p>
                <span className="font-medium">{processAllPreview.summary.subscriptionsWithWork}</span> of{' '}
                {processAllPreview.summary.totalSubscriptions} subscription(s) have pending steps.
              </p>
              <p className="mt-1">
                <span className="font-medium">{processAllPreview.summary.subscriptionsFullyComplete}</span>{' '}
                subscription(s) will be skipped entirely (sync, clone, and trigger already complete).
              </p>
            </div>

            <div className="mt-4 space-y-3">
              {processAllPreview.subscriptions.map((sub) => {
                const label = sub.subscriptionName || sub.subscriptionKey;
                return (
                  <div key={sub.subscriptionId} className="rounded-md border border-gray-200 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-medium text-gray-900">{label}</p>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          sub.allStepsComplete
                            ? 'bg-gray-100 text-gray-700'
                            : 'bg-indigo-100 text-indigo-800'
                        }`}
                      >
                        {sub.allStepsComplete ? 'Skip all steps' : 'Pending steps'}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {sub.providerName || sub.providerType} •{' '}
                      {maskSensitiveValue(sub.subscriptionKey)}
                    </p>
                    <ul className="mt-2 space-y-1 text-sm">
                      {sub.steps.map((step) => (
                        <li key={step.step} className={step.willRun ? 'text-gray-900' : 'text-gray-500'}>
                          {step.willRun ? '▶' : '○'} {step.label}
                          {!step.willRun && step.skipReason ? ` — ${step.skipReason}` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowProcessAllModal(false)}
                disabled={processingAll}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleProcessAllConfirm}
                disabled={processingAll || processAllPreview.summary.subscriptionsWithWork === 0}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {processingAll
                  ? 'Processing...'
                  : processAllPreview.summary.subscriptionsWithWork === 0
                    ? 'Nothing to Run'
                    : 'Start Process'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ClonePipelineModal
        isOpen={showVariablesModal}
        onClose={() => {
          if (!pipelineActionLoading) {
            setShowVariablesModal(false);
          }
        }}
        onConfirm={
          modalMode === 'clone' ? handleClonePipelineConfirm : handleTriggerPipelineConfirm
        }
        variables={engagementVariables}
        engagementName={
          selectedSubscriptionForPipeline
            ? `${engagementName} — ${selectedSubscriptionForPipeline.name}`
            : engagementName
        }
        isLoading={pipelineActionLoading || variablesLoading}
        mode={modalMode}
        pipelineName={
          selectedPipelineForTrigger?.pipelineName ||
          selectedPipelineForTrigger?.repositoryName ||
          selectedSubscriptionForPipeline?.name ||
          ''
        }
      />

      {resourceSyncTarget && (
        <ResourceSyncModal
          isOpen={!!resourceSyncTarget}
          onClose={() => setResourceSyncTarget(null)}
          engagementId={engagementId}
          engagementName={engagementName}
          subscriptionId={resourceSyncTarget.subscriptionId}
          subscriptionName={resourceSyncTarget.subscriptionName}
          onResourcesSynced={() => {
            setResourceSyncTarget(null);
            onRefreshEngagement?.();
          }}
        />
      )}

      {showLogsModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75"
              onClick={() => setShowLogsModal(false)}
            />
            <div
              className="relative flex w-full flex-col rounded-lg bg-white shadow-xl"
              style={{ maxWidth: '90vw', height: '85vh' }}
            >
              <div className="border-b border-gray-200 px-6 py-4">
                <h3 className="text-lg font-medium text-gray-900">Pipeline Execution Logs</h3>
                {selectedPipeline && (
                  <p className="mt-1 text-sm text-gray-500">
                    {selectedPipeline.pipelineName || selectedPipeline.repositoryName} — Run{' '}
                    {selectedPipeline.lastRunId}
                  </p>
                )}
              </div>
              <div className="flex-1 overflow-hidden px-6 py-4">
                {logsLoading ? (
                  <div className="flex h-full items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
                    <span className="ml-2 text-gray-600">Loading logs...</span>
                  </div>
                ) : logs ? (
                  <div className="flex h-full flex-col space-y-3">
                    <div className="flex gap-3">
                      {selectedPipeline && (
                        <div className="flex-1 rounded-md border border-gray-200 bg-gray-50 p-3">
                          <h4 className="mb-2 text-sm font-medium text-gray-900">Pipeline Information</h4>
                          <div className="space-y-1 text-sm text-gray-700">
                            <div>
                              <strong>Pipeline:</strong>{' '}
                              {selectedPipeline.pipelineName || selectedPipeline.repositoryName}
                            </div>
                            <div>
                              <strong>Run ID:</strong> {selectedPipeline.lastRunId}
                            </div>
                            {selectedPipeline.lastExecutionStatus && (
                              <div className="mt-2 flex items-center gap-2">
                                <strong>Status:</strong>
                                {getPipelineStatusBadge(selectedPipeline.lastExecutionStatus)}
                              </div>
                            )}
                            {logs.run?.url && (
                              <a
                                href={logs.run.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-2 inline-block text-xs text-blue-600 underline hover:text-blue-800"
                              >
                                View in Azure DevOps →
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="flex-1 rounded-md border border-gray-200 bg-gray-50 p-3">
                        <h4 className="mb-2 text-sm font-medium text-gray-900">Run Information</h4>
                        <div className="space-y-1 text-sm text-gray-600">
                          <div>
                            <strong>State:</strong> {logs.run?.state || '—'}
                          </div>
                          {logs.run?.result && (
                            <div>
                              <strong>Result:</strong> {logs.run.result}
                            </div>
                          )}
                          {logs.run?.createdDate && (
                            <div>
                              <strong>Started:</strong> {formatDateTime(logs.run.createdDate)}
                            </div>
                          )}
                          {logs.run?.finishedDate && (
                            <div>
                              <strong>Finished:</strong> {formatDateTime(logs.run.finishedDate)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-1 overflow-hidden rounded-md border border-gray-200">
                      <div className="w-1/4 overflow-y-auto border-r border-gray-200 bg-gray-50">
                        {logs.logs?.length > 0 ? (
                          <div className="p-2">
                            {logs.logs.map((log: any) => (
                              <button
                                key={log.id}
                                type="button"
                                onClick={() => setSelectedJobId(log.id)}
                                className={`mb-1 w-full rounded p-3 text-left text-sm ${
                                  selectedJobId === log.id
                                    ? 'border-l-4 border-blue-600 bg-blue-100 text-blue-900'
                                    : 'border-l-4 border-transparent bg-white text-gray-700 hover:bg-gray-100'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  {getJobStatusIcon(log.result, log.state)}
                                  <span className="truncate font-mono font-medium">{log.name}</span>
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="p-4 text-center text-sm text-gray-500">No jobs available</div>
                        )}
                      </div>
                      <div className="flex-1 overflow-y-auto bg-gray-900">
                        {selectedJobId !== null && logs.logs ? (
                          (() => {
                            const selectedLog = logs.logs.find((log: any) => log.id === selectedJobId);
                            if (!selectedLog?.lines?.length) {
                              return (
                                <div className="p-4 text-sm text-gray-400">No log lines available</div>
                              );
                            }
                            return (
                              <div className="p-3">
                                <div className="sticky top-0 mb-2 border-b border-gray-700 bg-gray-800 p-2">
                                  <span className="font-mono text-sm font-medium text-blue-400">
                                    {selectedLog.name}
                                  </span>
                                </div>
                                {selectedLog.lines.map((line: string, lineIndex: number) => (
                                  <div key={lineIndex} className="font-mono text-xs text-green-400">
                                    <span className="mr-3 select-none text-gray-500">
                                      {String(lineIndex + 1).padStart(4, ' ')}
                                    </span>
                                    {line}
                                  </div>
                                ))}
                              </div>
                            );
                          })()
                        ) : (
                          <div className="p-4 text-sm text-gray-400">Select a job to view logs</div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="py-8 text-center text-gray-500">Failed to load logs</div>
                )}
              </div>
              <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
                <button
                  type="button"
                  onClick={() => setShowLogsModal(false)}
                  className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
